// Admin › Help › Images: every image and clip the articles can use, newest
// first. Upload one with its alt text, the no-personal-data tick and the Scout
// software it was taken on (which later flags a stale screenshot); a GIF is a
// clip, and its still frame is made here from its first frame. The same file
// twice is stored once. docs/wip/SCOUT_HELP.md

import { useEffect, useMemo, useState } from 'react';
import { api } from '../../../services/api';
import { stillFrame } from './stillFrame';
import { TickBox } from './TickBox';
import {
  APPS,
  MEDIA_LIMITS,
  errorText,
  useHelpMedia,
  useUpdateMedia,
  useUploadMedia,
  type HelpMedia,
} from './data/help';

const input = 'min-h-[44px] w-full rounded-[10px] border border-slate-400/20 bg-slate-900/60 px-3 text-[15px] text-white placeholder:text-slate-500 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-500/40';
const label = 'flex flex-col gap-1.5 text-sm font-medium text-slate-300';
const button = 'inline-flex min-h-[44px] items-center rounded-[10px] px-4 text-[15px] font-semibold focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500 disabled:opacity-40';

const KINDS: Record<string, 'image' | 'clip'> = { 'image/png': 'image', 'image/jpeg': 'image', 'image/webp': 'image', 'image/gif': 'clip' };
const mb = (bytes: number) => (bytes / 1e6).toFixed(1);
/** A file's size as people read it: KB under 100 KB, MB above. */
const size = (bytes: number) => (bytes < 100_000 ? `${Math.max(1, Math.round(bytes / 1000))} KB` : `${mb(bytes)} MB`);
const VERSION = /^\d+\.\d+\.\d+$/;
const appName = (key: string) => APPS.find((a) => a.key === key)?.name ?? key;
const objectUrl = (b: Blob | null | undefined) => (b && typeof URL.createObjectURL === 'function' ? URL.createObjectURL(b) : undefined);

interface Fields {
  alt: string;
  noPersonalData: boolean;
  app: string;
  version: string;
}

/** The software a file was taken on, when both parts are given; `false` when only one is. */
function takenOnOf(f: Fields): { app: string; version: string } | null | false {
  if (!f.app && !f.version.trim()) return null;
  return f.app && VERSION.test(f.version.trim()) ? { app: f.app, version: f.version.trim() } : false;
}

function MediaFields({ value, onChange }: { value: Fields; onChange: (f: Fields) => void }) {
  return (
    <div className="space-y-3">
      <label className={label}>
        <span className="flex justify-between">Alt text <span className={`font-normal ${value.alt.length > 300 ? 'text-amber-300' : 'text-slate-500'}`}>{value.alt.length} / 300</span></span>
        <textarea aria-label="Alt text" className={`${input} min-h-[64px] py-2.5`} rows={2} value={value.alt} onChange={(e) => onChange({ ...value, alt: e.target.value })} />
      </label>
      <label className="flex min-h-[44px] items-center gap-3 text-[15px] text-slate-200">
        <TickBox checked={value.noPersonalData} onChange={(on) => onChange({ ...value, noPersonalData: on })} />
        Shows no personal data: no email, name, QR code or serial
      </label>
      <div className="grid grid-cols-[minmax(0,1fr)_8rem] gap-2">
        <label className={label}>
          Taken on
          <select className={input} value={value.app} onChange={(e) => onChange({ ...value, app: e.target.value })}>
            <option value="">Not recorded</option>
            {APPS.map((a) => <option key={a.key} value={a.key}>{a.name}</option>)}
          </select>
        </label>
        <label className={label}>
          Version
          <input className={`${input} font-mono`} value={value.version} placeholder="1.14.0" onChange={(e) => onChange({ ...value, version: e.target.value })} />
        </label>
      </div>
    </div>
  );
}

/** Why a set of fields can't be sent yet, or null. */
function fieldsProblem(f: Fields): string | null {
  if (!f.alt.trim()) return 'Add alt text: what the image shows, for someone who cannot see it.';
  if (f.alt.length > 300) return 'Alt text is at most 300 characters.';
  if (takenOnOf(f) === false) return 'Give both the app and its version (like 1.14.0), or neither.';
  return null;
}

export function HelpImagesPage() {
  const media = useHelpMedia();
  const [adding, setAdding] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const list = useMemo(() => (media.data ?? []).filter((m) => m.kind !== 'video'), [media.data]);
  const images = list.filter((m) => m.kind === 'image').length;
  const clips = list.length - images;

  return (
    <div className="max-w-[1120px] space-y-7 px-4 pb-16 pt-6 sm:px-6 lg:px-10 lg:pt-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="space-y-1.5">
          <h1 className="text-[26px] font-bold leading-none tracking-tight text-white">Images</h1>
          <p className="text-[15px] text-slate-400">
            {images} image{images === 1 ? '' : 's'} · {clips} clip{clips === 1 ? '' : 's'}
          </p>
        </div>
        {!adding && (
          <button type="button" onClick={() => { setAdding(true); setDone(null); }} className={`${button} border border-slate-400/25 text-white hover:bg-slate-400/10`}>
            Upload
          </button>
        )}
      </div>

      {done && <p role="status" className="rounded-xl border border-emerald-400/30 bg-emerald-400/10 px-4 py-3 text-[15px] text-emerald-100">{done}</p>}
      {adding && <UploadForm onDone={(message) => { setAdding(false); setDone(message); }} onCancel={() => setAdding(false)} />}

      {media.isLoading && <p className="text-[15px] text-slate-400">Loading…</p>}
      {media.isError && <p className="text-[15px] text-red-400">Couldn't load the images.</p>}
      {media.isSuccess && list.length === 0 && <p className="text-[15px] text-slate-400">Nothing uploaded yet.</p>}
      <ul className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((m) => <MediaCard key={m.id} m={m} />)}
      </ul>
    </div>
  );
}

function UploadForm({ onDone, onCancel }: { onDone: (message: string) => void; onCancel: () => void }) {
  const upload = useUploadMedia();
  const [file, setFile] = useState<File | null>(null);
  const [still, setStill] = useState<{ blob: Blob | null; error: string | null; making: boolean }>({ blob: null, error: null, making: false });
  const [fields, setFields] = useState<Fields>({ alt: '', noPersonalData: false, app: '', version: '' });

  const kind = file ? KINDS[file.type] : undefined;
  const preview = useMemo(() => objectUrl(file), [file]);
  const stillUrl = useMemo(() => objectUrl(still.blob), [still.blob]);
  useEffect(() => () => { if (preview) URL.revokeObjectURL(preview); }, [preview]);
  useEffect(() => () => { if (stillUrl) URL.revokeObjectURL(stillUrl); }, [stillUrl]);

  const choose = (f: File | null) => {
    setFile(f);
    upload.reset();
    setStill({ blob: null, error: null, making: f?.type === 'image/gif' });
    if (f?.type === 'image/gif') {
      stillFrame(f).then(
        (blob) => setStill({ blob, error: null, making: false }),
        (err: unknown) => setStill({ blob: null, error: err instanceof Error ? err.message : 'Couldn\'t make the still frame', making: false }),
      );
    }
  };

  const problem = !file
    ? null
    : !kind
      ? 'Choose a PNG, JPEG or WebP image, or a GIF clip.'
      : file.size > MEDIA_LIMITS[kind]
        ? `This ${kind} is ${mb(file.size)} MB; the limit is ${MEDIA_LIMITS[kind] / 1e6} MB.`
        : still.error ?? fieldsProblem(fields);
  const send = () => {
    const takenOn = takenOnOf(fields);
    if (!file || !kind || takenOn === false) return;
    upload.mutate(
      { kind, file, still: kind === 'clip' ? still.blob ?? undefined : undefined, alt: fields.alt.trim(), noPersonalData: fields.noPersonalData, takenOn },
      { onSuccess: (r) => onDone(r.reused ? `That file is already here, as "${r.media.alt}". Nothing new was stored.` : 'Uploaded.') },
    );
  };

  return (
    <div className="space-y-4 rounded-2xl border border-slate-400/15 bg-slate-800/45 px-5 py-5">
      <p className="text-base font-semibold text-white">Upload an image or clip</p>
      <p className="text-[15px] text-slate-400">
        Images: PNG, JPEG or WebP, up to {MEDIA_LIMITS.image / 1e6} MB. Clips: a short looping GIF with no sound, up to {MEDIA_LIMITS.clip / 1e6} MB; both go in the Scout's offline copy.
      </p>
      <label className={label}>
        File
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp,image/gif"
          className="text-[15px] text-slate-300 file:mr-3 file:min-h-[44px] file:rounded-[10px] file:border file:border-slate-400/25 file:bg-transparent file:px-4 file:text-[15px] file:font-semibold file:text-white"
          onChange={(e) => choose(e.target.files?.[0] ?? null)}
        />
      </label>
      {file && kind && (
        <div className="flex flex-wrap gap-4">
          <figure className="space-y-1.5">
            <img src={preview} alt="" className="max-h-48 max-w-full rounded-lg border border-slate-400/20 bg-slate-950 object-contain" />
            <figcaption className="text-[14px] text-slate-400">{kind === 'clip' ? 'Clip' : 'Image'} · {size(file.size)}</figcaption>
          </figure>
          {kind === 'clip' && (
            <figure className="space-y-1.5">
              {still.making && <p className="text-[15px] text-slate-400">Making the still frame…</p>}
              {still.blob && <img src={stillUrl} alt="Still frame" className="max-h-48 max-w-full rounded-lg border border-slate-400/20 bg-slate-950 object-contain" />}
              {still.blob && <figcaption className="text-[14px] text-slate-400">Still frame, shown when reduced motion is on · {size(still.blob.size)}</figcaption>}
            </figure>
          )}
        </div>
      )}
      <MediaFields value={fields} onChange={setFields} />
      {problem && <p className="text-[15px] text-amber-300">{problem}</p>}
      {upload.isError && <p role="alert" className="text-[15px] text-red-300">{errorText(upload.error)}</p>}
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={send}
          disabled={!file || !kind || !!problem || still.making || upload.isPending}
          className={`${button} bg-cyan-600 text-white hover:bg-cyan-500`}
        >
          {upload.isPending ? 'Uploading…' : kind === 'clip' ? 'Upload clip' : 'Upload image'}
        </button>
        <button type="button" onClick={onCancel} className={`${button} text-slate-300 hover:bg-slate-400/10`}>Cancel</button>
      </div>
    </div>
  );
}

function MediaCard({ m }: { m: HelpMedia }) {
  const update = useUpdateMedia();
  const [editing, setEditing] = useState<Fields | null>(null);
  const src = api.adminHelpMediaUrl(m.id, m.kind === 'clip' && !!m.posterKey);
  const problem = editing ? fieldsProblem(editing) : null;
  const save = () => {
    const takenOn = editing ? takenOnOf(editing) : false;
    if (!editing || takenOn === false) return;
    update.mutate({ id: m.id, patch: { alt: editing.alt.trim(), noPersonalData: editing.noPersonalData, takenOn } }, { onSuccess: () => setEditing(null) });
  };

  return (
    <li aria-label={m.alt || 'No alt text'} className="overflow-hidden rounded-2xl border border-slate-400/15 bg-slate-800/45">
      <div className="relative aspect-[16/10] bg-slate-950">
        <img src={src} alt={m.alt} loading="lazy" className="h-full w-full object-contain" />
        {m.kind === 'clip' && <span className="absolute left-2.5 top-2.5 rounded-md bg-slate-900/90 px-2 py-0.5 text-[13px] font-semibold text-white">Clip</span>}
      </div>
      <div className="space-y-1.5 px-4 py-3">
        {editing ? (
          <div className="space-y-3">
            <MediaFields value={editing} onChange={setEditing} />
            {problem && <p className="text-[15px] text-amber-300">{problem}</p>}
            {update.isError && <p role="alert" className="text-[15px] text-red-300">{errorText(update.error)}</p>}
            <div className="flex gap-2">
              <button type="button" disabled={!!problem || update.isPending} onClick={save} className={`${button} bg-cyan-600 text-white hover:bg-cyan-500`}>Save</button>
              <button type="button" onClick={() => { setEditing(null); update.reset(); }} className={`${button} text-slate-300 hover:bg-slate-400/10`}>Cancel</button>
            </div>
          </div>
        ) : (
          <>
            <p className="text-[15px] text-slate-200">{m.alt || <span className="text-amber-300">No alt text yet</span>}</p>
            <p className="text-[14px] text-slate-400">
              {m.kind === 'clip' ? 'Clip' : 'Image'}{m.width && m.height ? ` · ${m.width} × ${m.height}` : ''} · {size(m.bytes)}
            </p>
            {m.takenOn
              ? <p className="text-[14px] text-slate-400">Taken on {appName(m.takenOn.app)} {m.takenOn.version}</p>
              : <p className="text-[14px] text-amber-300">No Scout software recorded</p>}
            {!m.noPersonalData && <p className="text-[14px] text-amber-300">Not yet ticked as showing no personal data</p>}
            <button
              type="button"
              className="min-h-[44px] text-[15px] font-semibold text-cyan-300 hover:text-cyan-200"
              onClick={() => setEditing({ alt: m.alt, noPersonalData: m.noPersonalData, app: m.takenOn?.app ?? '', version: m.takenOn?.version ?? '' })}
            >
              Edit
            </button>
          </>
        )}
      </div>
    </li>
  );
}
